import React from 'react';
import type { Page, Component } from '../ComponentEditModal.types';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { PageSelector } from '../shared/PageSelector';

interface PromoBannerData {
  text?: string;
  linkText?: string;
  linkUrl?: string;
  linkTarget?: '_self' | '_blank';
  dismissible?: boolean;
  position?: 'inline';
  customColors?: {
    background?: string;
    text?: string;
    linkText?: string;
    dismissButton?: string;
  };
}

interface PromoBannerEditorProps {
  formData: PromoBannerData;
  setFormData: (data: PromoBannerData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
}

export const PromoBannerEditor: React.FC<PromoBannerEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  currentPageComponents,
}) => {
  const data = formData;
  const themeColors = useCustomColors();

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('promo-banner', themeColors);

  return (
    <div className="space-y-6">
      {/* Banner Text */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Banner Text *</label>
        <input
          type="text"
          value={data.text || ''}
          onChange={e => setFormData({ ...data, text: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Free shipping on orders over $50!"
          required
          maxLength={200}
        />
        <p className="mt-1 text-xs text-gray-500">
          Keep it short and impactful (max 200 characters)
        </p>
      </div>

      {/* Link Section */}
      <div className="border-t pt-6">
        <h3 className="text-md font-medium text-gray-900 mb-4">Link (Optional)</h3>

        <div className="space-y-4">
          {/* Link Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Link Text</label>
            <input
              type="text"
              value={data.linkText || ''}
              onChange={e => setFormData({ ...data, linkText: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Shop Now"
              maxLength={50}
            />
          </div>

          {/* Link URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Link URL</label>
            <PageSelector
              value={data.linkUrl || ''}
              onChange={url => setFormData({ ...data, linkUrl: url })}
              pages={pages}
              isLoadingPages={isLoadingPages}
              currentPageComponents={currentPageComponents}
            />
          </div>

          {/* Link Target */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Open Link In</label>
            <select
              value={data.linkTarget || '_self'}
              onChange={e =>
                setFormData({ ...data, linkTarget: e.target.value as '_self' | '_blank' })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="_self">Same Tab</option>
              <option value="_blank">New Tab</option>
            </select>
          </div>
        </div>
      </div>

      {/* Behavior Options */}
      <div className="border-t pt-6">
        <h3 className="text-md font-medium text-gray-900 mb-4">Behavior</h3>

        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={data.dismissible !== false}
            onChange={e => setFormData({ ...data, dismissible: e.target.checked })}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <div>
            <span className="text-sm font-medium text-gray-700">Allow visitors to dismiss</span>
            <p className="text-xs text-gray-500">
              Shows an X button. Dismissed state is saved in browser.
            </p>
          </div>
        </label>
      </div>

      {/* Color Customization */}
      <ColorCustomizationPanel
        title="Color Customization"
        description="Customize banner colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
      />
    </div>
  );
};
